import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "../supabaseClient";

export default function SellerDashboard() {
  const [searchParams] = useSearchParams();

  // Auth & Partner State
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [storeCode, setStoreCode] = useState("");
  const [partner, setPartner] = useState(null);

  // Transaction State
  const [userIdentifier, setUserIdentifier] = useState("");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  // --- NFC AUTOMATION LOGIC ---
  useEffect(() => {
    const scannedId = searchParams.get("user_id");
    
    if (scannedId) {
      console.log("✅ NFC Scan Detected ID:", scannedId);
      setUserIdentifier(scannedId);
      
      if (isAuthenticated) {
        setMessage("🎴 New card detected!");
        setTimeout(() => setMessage(""), 3000);
      }
    }
  }, [searchParams, isAuthenticated]);

  const handleVerifyCode = async () => {
    if (!storeCode) {
      setMessage("Please enter a code.");
      return;
    }
    setLoading(true);
    setMessage("");

    try {
      const { data, error } = await supabase
        .from("partners")
        .select("*")
        .eq("code", storeCode)
        .single();

      if (error || !data) {
        setMessage("❌ Invalid Store Code");
      } else {
        setPartner(data);
        setIsAuthenticated(true);
        setMessage("");
      }
    } catch (err) {
      setMessage("⚠️ Connection error.");
    } finally {
      setLoading(false);
    }
  };

  const handleAddPoints = async () => {
    if (!userIdentifier || !amount) {
      setMessage("Please enter Customer ID/Email and amount.");
      return;
    }

    setLoading(true);
    setMessage("");

    try {
      let { data: profile, error: fetchError } = await supabase
        .from("profiles")
        .select("*")
        .or(`user_id.eq.${userIdentifier},email.eq.${userIdentifier}`)
        .single();

      if (fetchError || !profile) {
        setMessage("Customer not found!");
        setLoading(false);
        return;
      }

      const pointsToAdd = parseInt(amount);

      const { error: updateError } = await supabase
        .from("profiles")
        .update({ points: (profile.points || 0) + pointsToAdd })
        .eq("user_id", profile.user_id);

      if (updateError) throw updateError;

      const { error: transactionError } = await supabase
        .from("transactions")
        .insert([
          {
            user_id: profile.user_id,
            amount: pointsToAdd,
            points_awarded: pointsToAdd,
            note: `${partner.name}: ${note || "Purchase"}`,
            partner_id: partner.id
          },
        ]);

      if (transactionError) throw transactionError;

      setMessage(`✅ Added ${pointsToAdd} points to ${profile.full_name}!`);
      setUserIdentifier("");
      setAmount("");
      setNote("");
    } catch (err) {
      console.error(err);
      setMessage(`⚠️ Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const styles = {
    container: { minHeight: "100vh", backgroundColor: "#f4f4f4", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "1.5rem", fontFamily: "'Segoe UI', Roboto, sans-serif" },
    card: { backgroundColor: "white", padding: "2rem", borderRadius: "16px", boxShadow: "0 10px 25px rgba(0,0,0,0.1)", width: "100%", maxWidth: "400px", boxSizing: "border-box" },
    input: { width: "100%", padding: "0.8rem", marginBottom: "1rem", borderRadius: "8px", border: "1px solid #ddd", fontSize: "1rem", boxSizing: "border-box" },
    label: { display: "block", marginBottom: "0.4rem", fontWeight: "600", color: "#444", fontSize: "0.9rem" },
    button: { width: "100%", padding: "1rem", backgroundColor: "#246e31", color: "white", border: "none", borderRadius: "8px", cursor: "pointer", fontWeight: "bold", fontSize: "1rem", marginTop: "0.5rem" },
    logo: { width: "90px", height: "90px", borderRadius: "50%", objectFit: "cover", marginBottom: "1rem", border: "3px solid #246e31", backgroundColor: "white" },
    secondaryBtn: { background: "none", border: "none", color: "#666", textDecoration: "underline", cursor: "pointer", marginTop: "1rem", fontSize: "0.8rem" },
    clearTag: { fontSize: "0.7rem", color: "#d32f2f", cursor: "pointer", float: "right", textDecoration: "underline" }
  };

  if (!isAuthenticated) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={{ textAlign: "center" }}>
            <h2 style={{ color: "#246e31", marginTop: 0 }}>Terminal Login</h2>
            <p style={{ color: "#666", marginBottom: "1.5rem" }}>Enter your partner security code</p>
          </div>
          <input
            type="password"
            placeholder="Store Pin Code"
            value={storeCode}
            onChange={(e) => setStoreCode(e.target.value)}
            style={styles.input}
          />
          <button onClick={handleVerifyCode} disabled={loading} style={styles.button}>
            {loading ? "Verifying..." : "Unlock Terminal"}
          </button>
          {message && <p style={{ color: "red", textAlign: "center", marginTop: "1rem" }}>{message}</p>}
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={{ textAlign: "center", marginBottom: "1.5rem" }}>
        {partner.logo_url && <img src={partner.logo_url} alt="Logo" style={styles.logo} />}
        <h1 style={{ color: "#246e31", margin: 0 }}>{partner.name}</h1>
        <p style={{ color: "#666", margin: "5px 0" }}>Point Award Terminal</p>
      </div>
      <div style={styles.card}>
        <div style={{ overflow: "hidden" }}>
          <label style={styles.label}>
            Customer Identity 
            {userIdentifier && <span onClick={() => setUserIdentifier("")} style={styles.clearTag}>Clear</span>}
          </label>
        </div>
        <input
          type="text"
          placeholder="Scanned via NFC or Email"
          value={userIdentifier}
          onChange={(e) => setUserIdentifier(e.target.value)}
          style={{...styles.input, backgroundColor: userIdentifier.length > 20 ? "#e8f5e9" : "white"}}
        />
        <label style={styles.label}>Points to Award</label>
        <input type="number" placeholder="Enter amount" value={amount} onChange={(e) => setAmount(e.target.value)} style={styles.input} />
        <label style={styles.label}>Item / Note</label>
        <input type="text" placeholder="e.g. Reusable Cup Coffee" value={note} onChange={(e) => setNote(e.target.value)} style={styles.input} />
        <button onClick={handleAddPoints} disabled={loading} style={styles.button}>{loading ? "Processing..." : "Confirm & Award Points"}</button>
        {message && <p style={{ marginTop: "1.2rem", textAlign: "center", color: message.includes("✅") ? "#1b5e20" : "#b71c1c", fontWeight: "500" }}>{message}</p>}
      </div>
      <button onClick={() => setIsAuthenticated(false)} style={styles.secondaryBtn}>Log out of {partner.name}</button>
    </div>
  );
}